import { useEffect, useState } from 'react';

import { ChatStepAction, ChatStepState } from './ChatStepActions.types';

// States that actually allow a submission. View must never trigger a submission.
const SUBMITABLE_STATES = [ChatStepState.PENDING, ChatStepState.EDIT];

export interface ChatStepConfigProps<T> {
  /**
   * Defines whether the step has additional edit and view states or is only submitable once
   */
  isEditable: boolean;
  /**
   * Defines whether the step is already completed and buttons should be disabled therefore
   */
  isCompleted?: boolean;
  /**
   * Sets the initial state for the step transitions.
   *
   * Defaults to `ChatStepState.PENDING` if not `isCompleted` is `true`.
   * If is `isCompleted` is `true` and `isEditable` is `true`, the default state is set to `ChatStepState.VIEW`.
   */
  startState?: ChatStepState;
  /**
   * Callback by the triggered action to perform operations upon finally.\
   * The `data` in `details` is only available for the submit action
   * All other actions should rely on the component's state data
   */
  onAction?: (action: ChatStepAction, details: { state: ChatStepState; data?: T }) => Promise<void>;
  /**
   * If a `formId` is present, an `onSubmit` function will be returned in the config.
   * That `onSubmit` can be used by `react-hook-form`'s handleSubmit finally. \
   * The `formId` is passed through and should ensure submit buttons in
   * `ChatStepState.PENDING` and `ChatStepState.EDIT` are used with that `formId`.
   */
  formId?: string;
}

export type HandleActionFn<T> = (action: ChatStepAction, data?: T) => Promise<void>;

export interface ChatStepActionBaseConfig<T> {
  /**
   * The current action state of the chat step
   */
  state: ChatStepState;
  /**
   * This function controls the proper transition between the states depending on optional actions
   * in `onAction` callback. \
   * It is triggered by action buttons' onClick with the respective action indicator or is internally
   * called by `onSubmit` during form submission.
   */
  handleAction: HandleActionFn<T>;
  /**
   * Defines whether the buttons should be disabled. Depends on the `isCompleted` prop
   */
  disabled: boolean;
  /**
   * Defines whether the content of the step should be switched to a read-only mode.
   * Depends on the `isCompleted` prop and state being `ChatStepState.VIEW
   */
  readOnly: boolean;
}

interface ChatStepActionConfigWithSubmit<T> extends ChatStepActionBaseConfig<T> {
  /**
   * Callback used by `react-hook-form`'s handleSubmit function.
   * @param data
   * @returns
   */
  onSubmit: (data: T) => Promise<void>;
  /**
   *  The passed through `formId` should ensure submit buttons in `ChatStepState.PENDING`
   * and `ChatStepState.EDIT` are used with that `formId`.
   */
  formId: string;
}

// Using Function Overloads for Proper Type Inference
export function useChatStepState<T>(
  config: { formId: string } & ChatStepConfigProps<T>,
): ChatStepActionConfigWithSubmit<T>;
export function useChatStepState<T>(config: ChatStepConfigProps<T>): ChatStepActionBaseConfig<T>;

// Actual hook Implementation
export function useChatStepState<T>(
  config: ChatStepConfigProps<T>,
): ChatStepActionBaseConfig<T> | ChatStepActionConfigWithSubmit<T> {
  const { isEditable, isCompleted, startState, onAction, formId } = config;

  if (!isEditable && startState && [ChatStepState.EDIT, ChatStepState.VIEW].includes(startState)) {
    throw new Error('Only editable steps can have an initial state of EDIT or VIEW');
  }

  // if completed but we have an edit option, we should be in disabled view mode
  const [state, setState] = useState<ChatStepState>(ChatStepState.PENDING);

  // Setting initial state based on props when mounting
  useEffect(() => {
    if (startState) {
      setState(startState);
    } else {
      setState(isCompleted && isEditable ? ChatStepState.VIEW : ChatStepState.PENDING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // data is only populated when a form is submitted
  const handleAction = async (action: ChatStepAction, data?: T) => {
    // Safe Guard: No action if already completed
    // Any button should get disabled by returned prop already
    if (isCompleted) {
      return;
    }

    try {
      await onAction?.(action, { state, data });
    } catch (error) {
      // NOOP - if the actual action throws an error, we skip changing the state
      return;
    }

    switch (action) {
      case ChatStepAction.NEXT:
      case ChatStepAction.SKIP:
      case ChatStepAction.SUBMIT:
        if (isEditable) setState(ChatStepState.VIEW);
        break;
      case ChatStepAction.EDIT:
        if (isEditable) setState(ChatStepState.EDIT);
        break;
      case ChatStepAction.SAVE:
      case ChatStepAction.CANCEL:
        setState(ChatStepState.VIEW);
        break;
    }
  };

  const onSubmit = async (data: T) => {
    // Safe Guard: Only PENDING and EDIT states should be able to submit
    if (!SUBMITABLE_STATES.includes(state)) {
      return;
    }
    await handleAction(ChatStepAction.SUBMIT, data);
  };

  return {
    state,
    handleAction,
    disabled: isCompleted ?? false,
    readOnly: isCompleted || state === ChatStepState.VIEW,
    ...(formId ? { onSubmit, formId } : {}),
  };
}
